'use client';
import React from 'react';
import { Input } from 'antd';
import { SessionContext } from '@benzinga/session-context';
import styled from '@benzinga/themetron';
import { Note, NotesManager } from '@benzinga/notes-manager';
import { isString } from '@benzinga/utils';
import { TickerContext } from '../context';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import { PermissionedComponent } from '@benzinga/user-context';
import { TrackingManager } from '@benzinga/tracking-manager';

export const TickerNotesModule = () => (
  <PermissionedComponent permission={{ action: 'bzpro/widget/use', resource: 'watchlist' }}>
    {access => (access ? <TickerNotes /> : null)}
  </PermissionedComponent>
);

export const TickerNotes: React.FC = () => {
  const session = React.useContext(SessionContext);
  const ticker = React.useContext(TickerContext);
  const [notesState, setNotesState] = React.useState<{
    newText: string;
    notes: Note[];
    placeholder: string;
  }>({
    newText: '',
    notes: [],
    placeholder: `Type a new note...`,
  });
  const textAreaRef = React.useRef<TextAreaRef>(null);
  const addNoteButtonJustClicked = React.useRef<boolean>(false);
  const [isAddNoteFocused, setIsAddNoteFocused] = React.useState<boolean>(false);

  React.useEffect(() => {
    (async () => {
      const notesManager = session.getManager(NotesManager);
      const { ok: notes = [] } = await notesManager.getNotesFiltered(ticker.symbol);
      setNotesState(oldState => ({
        ...oldState,
        notes,
      }));
    })();
  }, [session, ticker.symbol]);

  React.useEffect(() => {
    if (isAddNoteFocused && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [isAddNoteFocused]);

  const onFocus = () => {
    setIsAddNoteFocused(true);
    ticker.setDialogMode(true);
  };

  const onBlur = () => {
    if (addNoteButtonJustClicked.current) {
      addNoteButtonJustClicked.current = false;
      if (isAddNoteFocused && textAreaRef.current) {
        textAreaRef.current.focus();
      }
    }
    setIsAddNoteFocused(false);
    ticker.setDialogMode(false);
  };

  const onNewNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e?.target?.value;
    addNoteButtonJustClicked.current = true;
    if (isString(val)) {
      setNotesState(oldState => ({ ...oldState, newText: val }));
      ticker.updateHeight();
    }
  };

  const onNewNote = async () => {
    const notesManager = session.getManager(NotesManager);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setNotesState(oldState => {
      if (oldState.newText.length > 0) {
        (async () => {
          await notesManager.createNote(ticker.symbol, notesState.newText);
          const { ok: notes = [] } = await notesManager.getNotesFiltered(ticker.symbol);
          session.getManager(TrackingManager).trackNoteEvent('add', {
            symbol: ticker.symbol,
          });
          setNotesState(oldState => ({
            ...oldState,
            notes,
          }));
          ticker.updateHeight();
        })();
        return {
          ...oldState,
          newText: '',
          placeholder: `Type a new note...`,
        };
      } else {
        ticker.updateHeight();
        return {
          ...oldState,
          placeholder: 'Note cannot be empty',
        };
      }
    });
  };

  return (
    <>
      <NoteHeaderDiv>
        <LatestNotesDiv>Latest Note</LatestNotesDiv>
      </NoteHeaderDiv>
      {notesState.notes?.[0]?.getText()?.length > 0 && <NoteBodyDiv>{notesState.notes?.[0]?.getText()}</NoteBodyDiv>}
      <NotesAddDiv>
        <NoteAddBorderDiv>
          <RowContainerDiv>
            {isAddNoteFocused ? (
              <Input.TextArea
                autoSize={true}
                onBlur={onBlur}
                onChange={onNewNoteChange}
                onFocus={onFocus}
                placeholder={notesState.placeholder}
                ref={textAreaRef}
                value={notesState.newText}
              />
            ) : (
              <Input
                onBlur={onBlur}
                onFocus={onFocus}
                placeholder={notesState.placeholder}
                value={notesState.newText}
              />
            )}
            <AddButtonContainerDiv>
              <AddButtonDiv onClick={onNewNote}>Add</AddButtonDiv>
            </AddButtonContainerDiv>
          </RowContainerDiv>
        </NoteAddBorderDiv>
      </NotesAddDiv>
    </>
  );
};

const NoteHeaderDiv = styled.div``;

const NoteBodyDiv = styled.div`
  margin: 5px 12px;
  max-width: 350px;
  white-space: normal;
`;

const LatestNotesDiv = styled.div`
  font-size: 1.2em;
  margin: 5px 12px;
`;

const NotesAddDiv = styled.div`
  margin-top: 2px;
`;

const NoteAddBorderDiv = styled.div`
  border-bottom-width: 1px;
  border-color: ${props => props.theme.colors.border};
  border-style: solid;
  margin-bottom: 5px;
  padding: 5px;
`;

const RowContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const AddButtonDiv = styled.div`
  background: ${props => props.theme.colors.brand};
  cursor: pointer;
  margin-bottom: 3px;
  margin-left: 4px;
  padding-bottom: 6px;
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 6px;
`;

const AddButtonContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;
